<template>
    <div class="verify-page">
        <div class="verify-page__container mb-3">
            <logos-container class="verify-page__logos"/>
            <h1 class="verify-page__title"> {{ $t('Two Factor Authentication')}}</h1>
            <p class="verify-page__text"> {{ $t('Enter the verification code to continue')}}</p>
        </div>
        <div class="verify-page__content">
            <div class="verify-page__card">
                <div class="verify-page__card-icon-container">
                    <div class="verify-page__card-icon">
                        <i class="fas fa-mobile-alt verify-page__card-icon-element"></i>
                    </div>
                </div>
                <div class="verify-page__card-content">
                    <div class="verify-page__card-text-container">
                        <span class="verify-page__card-title">
                            {{ $t('Authentication app') }}
                        </span>
                        <span class="verify-page__card-subtitle">
                            {{ $t('Open Google Authenticator or your verification app and use the code that appears') }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="verify-page__input mt-2 mb-3">
                <form @submit.prevent="handleValidateCode()">
                    <div class="mb-3">
                        <ui-input
                            id="code"
                            name="code" 
                            type="number"
                            placeholder="000000"
                            v-model="token"
                            :label="$t('Verification code')"
                            :field="v$.token"
                        />
                    </div>
                    <ui-button :loading="validatingCode" class="w-100 mb-0" type="submit" variant="primary" @click.prevent="handleValidateCode">
                        {{ $t('Validate code') }}
                    </ui-button>
                </form>
            </div>
            <div class="verify-page__other_methods">
                <b-button  variant="link" type="button" class="verify-page__other_methods_button" @click="showOtherMethods">
                    <i :class="show ? 'fas fa-angle-up' : 'fas fa-angle-down'"></i>
                    {{ $t("Don't have access to your app? Use another alternative method") }}
                </b-button>
                <div v-if="show" class="verify-page__options-list">
                    <p class="verify-page__options-text"> {{ $t('Select an option to receive the code through another method.')}} </p>
                    <option-button
                        :active="notificationType === 'email'"
                        :loading="loading && notificationType === 'email'"
                        :disabled="sendingCode"
                        :title="$t('Email')"
                        :subtitle="$t('Receive code by email')"
                        :loading-text="$t('Sending code...')"
                        inactive-icon="fas fa-envelope"
                        @click="sendOTP('email')"
                    />
                    <option-button
                        :active="notificationType === 'whatsapp'"
                        :loading="loading && notificationType === 'whatsapp'"
                        :disabled="sendingCode"
                        :title="$t('WhatsApp')"
                        :subtitle="$t('Receive code by WhatsApp')"
                        :loading-text="$t('Sending code...')"
                        inactive-icon="fab fa-whatsapp"
                        @click="sendOTP('whatsapp')"
                    />
                    <option-button
                        :active="notificationType === 'omni'"
                        :loading="loading && notificationType === 'omni'"
                        :disabled="sendingCode"
                        :title="$t('SMS')"
                        :subtitle="$t('Receive code by SMS')"
                        :loading-text="$t('Sending code...')"
                        inactive-icon="fas fa-sms"
                        @click="sendOTP('omni')"
                    />
                </div>
            </div>
        </div>
        <vue-recaptcha
        class="w-100"
        ref="validateCode"
        size="invisible"
        loadRecaptchaScript
        :sitekey="CONST.CAPTCHACODE"
        @verify="verify"
        />
    </div>
</template>

<script>
import LogosContainer from '@/components/ui/LogosContainer.vue';
import OptionButton from '@/components/ui/buttons/OptionButton.vue';
import UiInput from '@/components/ui/inputs/Input.vue';
import UiButton from '@/components/ui/buttons/Button.vue';
import VueRecaptcha from 'vue-recaptcha';

import webViewMixin from '@/plugins/mixin/web-view.mixin';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, minLength, helpers } from '@vuelidate/validators';
import { OMNI, WHATSAPP, OTP } from '@/constants/notification-types';

import recaptchaUtils from '@/utils/recaptcha.utils';

export default {
    components: { LogosContainer, OptionButton, UiInput, UiButton, VueRecaptcha },
    mixins: [webViewMixin],
    setup() {
       return {
            v$: useVuelidate(),
       };
    },
    data() {
        return {
            notificationTypes: {
                OMNI,
                WHATSAPP,
                OTP
            },
            show: false,
            token: '',
            notificationType: null,
            loading: false,
            validatingCode: false,
            sendingCode: false,
            user: null,
            loadingAccount: false,
        }
    },
    validations() {
        return {
            token: {
                required: helpers.withMessage(this.$t('validations.required', { field: this.$t('code') }), required),
                minLength: helpers.withMessage(({ $params }) => this.$t('This field must be a minimum of {min} characters', $params ), minLength(6)),
                maxLength: helpers.withMessage(({ $params }) => this.$t('This field must have a maximum of {max} characters', $params ), maxLength(6)),
                $autoDirty: true,
            },
        };
    },
    async mounted() {
        await this.getAccountDetails();
    },
    methods: {
        showOtherMethods() {
            this.show = !this.show;
        },
        async getAccountDetails() {
            try {
                this.loadingAccount = true;

                const { data } = await this.axiosAccount.get('/api/accounts/me');

                this.user = data;
            } catch(error) {
                this.showError(error);
            } finally {
                this.loadingAccount = false;
            }
        },
        async sendOTP(type) {
            try {
                this.sendingCode = true;
                this.loading = true;
                this.notificationType = type;
                const action = 'twofactor_verification';
                const payload = {
                    type: type,
                    action: action,
                    message: 'Two Factor Authentication',
                    from: 'Accounts Tendencys',
                };
                await this.axiosAccount.post('/api/notifications/tfa', payload);
                this.$toasted.global.successMessage('Code sent successfully');
            } catch (err) {
                this.showError(err);
            } finally {
                this.loading = false;
                this.sendingCode = false;
            }
        },
        handleValidateCode() {
            this.v$.$touch();
            if (this.v$.token.$invalid) {
                return;
            }
            this.validatingCode = true;
            if (!recaptchaUtils.isAvailable()) {
                this.verify('');
                return;
            }

            this.$refs.validateCode.reset();
            this.$refs.validateCode.execute();
        },
         async verify(token) {
            try {
                this.loading = true;

                this.v$.$touch();

                if (this.v$.$invalid) {
                    return;
                }

                const _token = `${this.user.email}:${this.token}`;
                const encodedToken = Buffer.from(_token).toString('base64');
                const config = { headers: { 'Authorization': `Basic ${encodedToken}` } };

                const payload = { token };

                const { data } = await this.axiosAccount.post('/api/login/tfa', payload, config);
                let { redirect_url } = data;
                const webview = this.$route.query.webview;

                if (webview) {
                    redirect_url = this.formatWebviewRedirectUrl(redirect_url);
                }

                window.location.replace(redirect_url);
            } catch(error) {
                this.showError(error);
            } finally {
                this.loading = false;
                this.validatingCode = false;
            }
        },
    }
} 
</script>

<style lang="scss">
.verify-page {
    align-items: center;

    &__container {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
    }

    &__logos {
        display: flex;
        justify-content: center;
    }
    &__title {
        font-size: 35px;
        font-weight: 600;
        text-align: center;
        margin: 20px;
    }

    &__text {
        color: #787F84;
        font-size: 16px;
        margin: 20px;
        text-align: center;
    }

    &__content {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
        align-items: center;
    }

    &__card {
        align-items: center;
        background-color: $accounts-secondary-100;
        border: 1px solid $accounts-secondary-300;
        border-radius: 8px;
        display: flex;
        gap: 12px;
        padding: 16px;
        width: 100%;
    }

    &__card-icon-container {
        flex-shrink: 0;
    }

    &__card-icon {
        align-items: center;
        background-color: var(--color-primary-800);
        border-radius: 50%;
        display: flex;
        height: 40px;
        justify-content: center;
        width: 40px;
    }

    &__card-icon-element {
        color: $white;
        font-size: 18px;
    }

    &__card-content {
        align-items: center;
        display: flex;
        flex: 1;
        justify-content: space-between;
    }

    &__card-text-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__card-title {
        color: #333333;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.4;
    }

    &__card-subtitle {
        color: $accounts-secondary;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
    }

    &__other_methods {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__other_methods_button {
        color: $accounts-secondary !important;
        font-size: 14px;
        font-weight: 400;
        align-items: center;
    }

    &__options-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        margin-top: 12px;
    }

    &__options-text {
        color: #787F84;
        font-size: 14px;
        margin-bottom: 8px;
        text-align: center;
    }
}
</style>